import logger from '../utils/logger';
// src/redux/userSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { Login, api, getPublicTranslations } from '../api/api';
import CustomAlert from '../components/CustomAlert';

export const loginUser = createAsyncThunk(
  'user/login',
  async ({ email, password }, { rejectWithValue }) => {
    try {
      const response = await Login(email, password);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'An error occurred';

      let translatedMessage = errorMessage;
      try {
        const saved = localStorage.getItem('translations');
        if (saved) {
          const translations = JSON.parse(saved);
          if (translations && translations[errorMessage]) {
            translatedMessage = translations[errorMessage];
          }
        }
      } catch (e) {
        logger.error('Failed to parse translations during login error handling', e);
      }

      CustomAlert.warning('', translatedMessage);
      return rejectWithValue(error.response?.data || errorMessage);
    }
  }
);

// Create logout asyncThunk
export const logoutUser = createAsyncThunk('user/logout', async (_, { rejectWithValue }) => {
  try {
    const response = await api.post('/logout', {});
    return response.data; // Return the response message
  } catch (error) {
    logger.error('logout error', error);
    return rejectWithValue(
      error.response?.data?.message || error.message || 'An error occurred during logout'
    );
  }
});
const getSavedTranslations = () => {
  try {
    const savedUser = localStorage.getItem('user');
    // Public site / Not logged in: Do not load previous user's translations
    if (!savedUser) return {};
    const saved = localStorage.getItem('translations');
    if (!saved || saved === 'undefined' || saved === 'null') return {};
    const parsed = JSON.parse(saved);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed;
    }
  } catch (e) {
    logger.warn('Invalid translations in localStorage', e);
    try {
      localStorage.removeItem('translations');
    } catch (err) {}
  }
  return {};
};

const getSavedUser = () => {
  try {
    const saved = localStorage.getItem('user');
    if (!saved || saved === 'undefined' || saved === 'null') return null;
    const parsed = JSON.parse(saved);
    if (parsed && parsed.id && parsed.email) return parsed;
  } catch (e) {
    logger.warn('Invalid user in localStorage', e);
    try {
      localStorage.removeItem('user');
    } catch (err) {}
  }
  return null;
};

const savedTranslations = getSavedTranslations();
const savedUser = getSavedUser();

export const fetchTranslations = createAsyncThunk(
  'user/fetchTranslations',
  async (langCode, { rejectWithValue }) => {
    try {
      const translations = await getPublicTranslations(langCode);
      return { translations, langCode };
    } catch (error) {
      return rejectWithValue('Failed to fetch translations');
    }
  }
);

// Add setUser action to set user data directly
export const userSlice = createSlice({
  name: 'user',
  initialState: {
    user: savedUser, // ✅ Restore user
    translations: savedTranslations, // ✅ localStorage se load
    direction: 'ltr', // ✅ RTL/LTR direction
    isRTL: false, // ✅ RTL flag
    lang: 'en', // ✅ Default language code
    selectedTranslations: null, // 🔄 Temporary storage during login transition
    loading: false,
    error: null,
    sessionChecked: false, // 🔄 Tracks if the initial session check has completed
  },
  reducers: {
    setUser: (state, action) => {
      const user = action.payload?.user ? action.payload.user : action.payload;
      state.user = user; // Save the user data in Redux
      try {
        if (user) {
          localStorage.setItem('user', JSON.stringify(user));
        } else {
          localStorage.removeItem('user');
          localStorage.removeItem('selectedLanguageId');
          localStorage.removeItem('translations');
        }
      } catch (e) {}
    },
    setTranslations: (state, action) => {
      state.translations = action.payload;
      state.selectedTranslations = null; // Clear temp if set directly
      try {
        localStorage.setItem('translations', JSON.stringify(action.payload)); // ✅ update bhi karo
      } catch (e) {}
    },
    setTranslationsTemp: (state, action) => {
      state.selectedTranslations = action.payload;
    },
    completeLoginTransition: (state) => {
      if (state.selectedTranslations && Object.keys(state.selectedTranslations).length > 0) {
        state.translations = state.selectedTranslations;
        try {
          localStorage.setItem('translations', JSON.stringify(state.selectedTranslations));
        } catch (e) {}
      }
      state.selectedTranslations = null;
    },
    setDirection: (state) => {
      state.direction = 'ltr';
      state.isRTL = false;
      try {
        localStorage.setItem('appDirection', 'ltr'); // ✅ persist direction
      } catch (e) {}
    },
    logout: (state) => {
      try {
        localStorage.removeItem('translations'); // ✅ clear karo
        localStorage.removeItem('user'); // ✅ remove user data too
        localStorage.removeItem('appDirection'); // ✅ clear direction
        localStorage.removeItem('selectedLanguageId'); // ✅ remove selectedLanguageId
      } catch (e) {}
      state.user = null;
      state.translations = {};
      state.lang = 'en';
      state.direction = 'ltr';
      state.isRTL = false;
      state.sessionChecked = true; // Still checked even if logged out
    },
    setSessionChecked: (state, action) => {
      state.sessionChecked = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload?.two_factor_required) {
          return;
        }
        state.user = action.payload?.user;
        state.selectedTranslations = action.payload?.translations || {}; // ✅ Store temporarily
        try {
          localStorage.setItem('user', JSON.stringify(action.payload?.user)); // ✅ Save user to localStorage
        } catch (e) {}
        state.sessionChecked = true; // ✅ Mark as checked
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.user = null; // Clear user state
      })
      .addCase(logoutUser.rejected, (state, action) => {
        state.error = action.payload; // Handle any logout error
      })
      .addCase(fetchTranslations.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchTranslations.fulfilled, (state, action) => {
        state.loading = false;
        state.translations = action.payload.translations;
        state.lang = action.payload.langCode;
        try {
          localStorage.setItem('translations', JSON.stringify(action.payload.translations));
        } catch (e) {}
      })
      .addCase(fetchTranslations.rejected, (state) => {
        state.loading = false;
      });
  },
});

export const {
  setUser,
  setTranslations,
  setTranslationsTemp,
  completeLoginTransition,
  setDirection,
  logout,
  setSessionChecked,
} = userSlice.actions;

export default userSlice.reducer;
